/* eslint-disable no-unused-vars */
import { useState, useMemo, useEffect } from 'react';
import { History, AlertCircle, CheckCircle, Calendar, X, Search, Clock, Shield, FileText, Download, User, BarChart2 } from 'lucide-react';
import { getUsedDays, isGreekHoliday} from '../utils/vacationUtils';
import DepartmentFilter from './DepartmentFilter';
import DatePicker, { registerLocale } from 'react-datepicker';
import el from 'date-fns/locale/el';
import 'react-datepicker/dist/react-datepicker.css';
import MassExportModal from './MassExportModal';

registerLocale('el', el);

const handlePrint = (request, dept) => {
  const printWindow = window.open('', '_blank');
  const dateStr = new Date().toLocaleDateString('el-GR');
  
  const workdayNames = request.breakdown.allDaysFormatted
    .filter(d => d.isWorkday)
    .map(d => d.fullName)
    .join(' - ');

  const html = `
    <html>
      <head>
        <title>Αίτηση Άδειας - ${request.user?.name || 'Υπάλληλος'}</title>
        <style>
          @page { size: auto; margin: 10mm; }
          body { font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif; padding: 20px; color: #1f2937; line-height: 1.4; font-size: 12px; }
          .header { border-bottom: 2px solid #3b82f6; padding-bottom: 10px; margin-bottom: 25px; display: flex; justify-content: space-between; align-items: flex-end; }
          .title { font-size: 20px; font-weight: 800; color: #111827; margin: 0; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
          .info-item { border-bottom: 1px solid #e5e7eb; padding-bottom: 5px; }
          .label { font-size: 9px; color: #6b7280; text-transform: uppercase; font-weight: 700; letter-spacing: 0.05em; margin-bottom: 2px; }
          .value { font-size: 14px; font-weight: 600; color: #111827; }
          .stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin: 30px 0; }
          .badge { background: #f0f7ff; border: 1px solid #c2e0ff; padding: 15px; border-radius: 12px; text-align: center; }
          .badge.secondary { background: #f9fafb; border: 1px solid #e5e7eb; }
          .badge-label { font-size: 10px; color: #1e40af; font-weight: 800; text-transform: uppercase; margin-bottom: 4px; }
          .badge.secondary .badge-label { color: #4b5563; }
          .badge-value { font-size: 28px; font-weight: 900; color: #1d4ed8; line-height: 1; }
          .badge.secondary .badge-value { color: #1f2937; }
          .workdays-list { font-size: 11px; color: #3b82f6; font-weight: 600; margin-top: 8px; }
          .reason-section { background: #f9fafb; border-left: 4px solid #d1d5db; padding: 15px; margin-top: 20px; border-radius: 0 8px 8px 0; }
          .reason-text { font-style: italic; color: #4b5563; font-size: 13px; }
          @media print { .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="title">ΕΓΚΕΚΡΙΜΕΝΗ ΑΙΤΗΣΗ ΑΔΕΙΑΣ</div>
            <div style="font-weight: 600; color: #3b82f6;">Σύστημα Διαχείρισης Προσωπικού</div>
          </div>
          <div style="text-align: right; font-size: 10px; color: #9ca3af;">Ημερομηνία Έκδοσης: ${dateStr}</div>
        </div>
        
        <div class="info-grid">
          <div class="info-item"><div class="label">Ονοματεπώνυμο</div><div class="value">${request.user?.name || '---'}</div></div>
          <div class="info-item"><div class="label">Τμήμα</div><div class="value">${dept?.name || '---'}</div></div>
          <div class="info-item"><div class="label">Από Ημερομηνία</div><div class="value">${request.startDate.toLocaleDateString('el-GR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div></div>
          <div class="info-item"><div class="label">Έως Ημερομηνία</div><div class="value">${request.endDate.toLocaleDateString('el-GR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div></div>
        </div>

        <div class="stats-grid">
          <div class="badge">
            <div class="badge-label">Εργάσιμες Ημέρες</div>
            <div class="badge-value">${request.daysGranted || request.workdays}</div>
            <div class="workdays-list">(${workdayNames})</div>
          </div>
          <div class="badge secondary">
            <div class="badge-label">Ημερολογιακές Ημέρες</div>
            <div class="badge-value">${request.calendarDays}</div>
            <div style="font-size: 10px; color: #9ca3af; margin-top: 5px;">Συνολική Διάρκεια</div>
          </div>
        </div>

        <div class="label" style="margin-bottom: 8px;">Αιτιολογία / Σχόλια</div>
        <div class="reason-section">
          <div class="reason-text">"${request.reason || 'Δεν υποβλήθηκε αιτιολογία.'}"</div>
        </div>

        <script>window.onload = function() { window.print(); setTimeout(function() { window.close(); }, 500); };</script>
      </body>
    </html>
  `;
  printWindow.document.write(html);
  printWindow.document.close();
};

const getDayBreakdown = (start, end, holidays) => {
  let workdays = 0, calendarDays = 0;
  const allDaysFormatted = [], foundHolidays = [];
  let current = new Date(start);
  const last = new Date(end);
  const fmtShort = new Intl.DateTimeFormat('el-GR', { weekday: 'short' });
  const fmtFull = new Intl.DateTimeFormat('el-GR', { weekday: 'long' });

  while (current <= last) {
    calendarDays++;
    const dayShort = fmtShort.format(current);
    const dayFull  = fmtFull.format(current);
    const dow      = current.getDay();
    const holiday  = isGreekHoliday(current, holidays);
    
    if (holiday) {
      foundHolidays.push(holiday);
      allDaysFormatted.push({ name: dayShort, fullName: dayFull, isWorkday: false });
    } else if (dow === 0 || dow === 6) {
      allDaysFormatted.push({ name: dayShort, fullName: dayFull, isWorkday: false });
    } else {
      workdays++;
      allDaysFormatted.push({ name: dayShort, fullName: dayFull, isWorkday: true });
    }
    current.setDate(current.getDate() + 1);
  }
  return { workdays, calendarDays, allDaysFormatted, foundHolidays: [...new Set(foundHolidays)] };
};

const FilterBar = ({ searchTerm, onSearch, departments, selectedDepartments, onDeptChange, startDate, endDate, onStartDate, onEndDate }) => (
  <div className="bg-white dark:bg-jewel-bg p-4 rounded-xl border border-gray-200 dark:border-jewel-border shadow-sm mb-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div>
        <label className="text-[10px] font-bold text-gray-400 dark:text-jewel-text-muted uppercase mb-2 block tracking-wider">Αναζήτηση Υπαλλήλου</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-jewel-text-muted" />
          <input
            type="text"
            placeholder="Αναζήτηση με όνομα..."
            value={searchTerm}
            onChange={e => onSearch(e.target.value)}
            className="pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg focus:ring-2 focus:ring-blue-500 w-full outline-none transition-all"
          />
        </div>
      </div>
      <div className="relative z-[60]">
        <label className="text-[10px] font-bold text-gray-400 dark:text-jewel-text-muted uppercase mb-2 block tracking-wider">Φίλτρο Τμήματος</label>
        <DepartmentFilter
          departments={departments}
          selectedDepartments={selectedDepartments}
          onDepartmentChange={onDeptChange}
        />
      </div>
      <div>
        <label className="text-[10px] font-bold text-gray-400 dark:text-jewel-text-muted uppercase mb-2 block tracking-wider">Εύρος Ημερομηνιών</label>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <DatePicker
              selected={startDate}
              onChange={onStartDate}
              placeholderText="Από"
              locale="el"
              dateFormat="dd/MM/yyyy"
              className="text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg p-2 w-full outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <span className="text-gray-400 dark:text-jewel-text-muted">—</span>
          <div className="relative flex-1">
            <DatePicker
              selected={endDate}
              onChange={onEndDate}
              placeholderText="Έως"
              locale="el"
              dateFormat="dd/MM/yyyy"
              className="text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg p-2 w-full outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {(startDate || endDate) && (
            <button 
              onClick={() => { onStartDate(null); onEndDate(null); }}
              className="p-2 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
              title="Καθαρισμός ημερομηνιών"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
);

const GrantedTab = ({ vacationRequests, userDatabase, departments, holidays }) => {
  const [selectedDepartments, setSelectedDepartments] = useState(departments.map(d => d.id));
  const [startDateFilter, setStartDateFilter] = useState(null);
  const [endDateFilter, setEndDateFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination State
  const ITEMS_PER_PAGE = 25;
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  // Reset pagination when filters change
  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
  }, [selectedDepartments, searchTerm, startDateFilter, endDateFilter]);

  const allGranted = useMemo(() => {
    if (!vacationRequests) return [];
    return vacationRequests
      .filter(req => (req.manager_granted || req.managerGranted) && req.status === 'approved' && (req.leave_type || req.leaveType) !== 'unpaid')
      .map(req => {
        const userId = req.user_id || req.userId;
        const user = userDatabase.find(u => Number(u.id) === Number(userId));
        const start = new Date(req.start_date || req.startDate);
        const end = new Date(req.end_date || req.endDate);
        const bd = getDayBreakdown(start, end, holidays);
        const usedInYear = getUsedDays(userId, vacationRequests, 'vacation', holidays);
        const totalAllowed = user?.totalDays || user?.total_days || 0;
        const remaining = Math.max(0, totalAllowed - usedInYear);
        

        return {
          id: req.id,
          user,
          deptId: user?.department_id || user?.departmentId,
          daysGranted: bd.workdays,
          workdays: bd.workdays,
          calendarDays: bd.calendarDays,
          breakdown: bd,
          reason: req.reason,
          grantedDate: new Date(req.reviewed_date || req.reviewedDate || req.created_at),
          startDate: start,
          endDate: end,
          exceededLimit: bd.workdays > remaining,
          excessDays: Math.max(0, bd.workdays - remaining),
          grantedBy: userDatabase.find(u => Number(u.id) === Number(req.reviewed_by || req.reviewedBy))
        };
      })
      .sort((a, b) => b.grantedDate - a.grantedDate);
  }, [vacationRequests, userDatabase, holidays]);

  const filtered = useMemo(() => allGranted.filter(g => {
    const matchesDept = selectedDepartments.includes(Number(g.deptId));
    const matchesSearch = !searchTerm || (g.user?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStart = !startDateFilter || g.startDate >= startDateFilter;
    const matchesEnd = !endDateFilter || g.endDate <= endDateFilter;
    return matchesDept && matchesSearch && matchesStart && matchesEnd;
  }), [allGranted, selectedDepartments, searchTerm, startDateFilter, endDateFilter]);

  const stats = useMemo(() => ({
    total: filtered.length,
    totalDays: filtered.reduce((acc, curr) => acc + curr.daysGranted, 0),
    exceeded: filtered.filter(g => g.exceededLimit).length,
    excessDays: filtered.reduce((acc, curr) => acc + curr.excessDays, 0)
  }), [filtered]);

  const visibleGrants = filtered.slice(0, visibleCount);

  const remaining = filtered.length - visibleCount;

  const getStatusStyles = () => {
    if (remaining >= 300) return "text-red-500 font-medium";
    if (remaining >= 200 && remaining < 300) return "text-orange-500 font-bold";
    if (remaining >= 100 && remaining < 200)   return "text-yellow-500 font-bold";
    if (remaining >= 50 && remaining < 100) return "text-teal-500 font-bold";
    if (remaining >= 10 && remaining < 50)   return "text-green-500 font-bold";
    return "text-black font-medium animate-bounce";
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Συνολικές Χορηγήσεις', value: stats.total, icon: FileText, color: 'text-blue-600 dark:text-jewel-teal-tint', bg: 'bg-blue-50 dark:bg-jewel-teal/25' },
          { label: 'Ημέρες Χορήγησης', value: stats.totalDays, icon: Calendar, color: 'text-purple-600 dark:text-jewel-ruby-tint', bg: 'bg-purple-50 dark:bg-jewel-ruby/25' },
          { label: 'Υπερβάσεις Ορίου', value: stats.exceeded, icon: AlertCircle, color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/40' },
          { label: 'Ημέρες Υπέρβασης', value: stats.excessDays, icon: BarChart2, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/40' }
        ].map((stat, idx) => (
          <div key={idx} className="bg-white dark:bg-jewel-bg p-4 rounded-xl border border-gray-200 dark:border-jewel-border shadow-sm transition-all hover:shadow-md">
            <div className="flex items-center justify-between mb-2">
              <stat.icon className={`h-4 w-4 ${stat.color}`} />
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${stat.bg} ${stat.color}`}>ΣΤΑΤΙΣΤΙΚΑ</span>
            </div>
            <p className="text-[10px] font-bold text-gray-400 dark:text-jewel-text-muted uppercase tracking-wider">{stat.label}</p>
            <p className={`text-2xl font-black ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      <FilterBar searchTerm={searchTerm} onSearch={setSearchTerm} departments={departments} selectedDepartments={selectedDepartments} onDeptChange={setSelectedDepartments} startDate={startDateFilter} endDate={endDateFilter} onStartDate={setStartDateFilter} onEndDate={setEndDateFilter} />

      <div className="space-y-4">
        {visibleGrants.map(grant => {
          const dept = departments.find(d => Number(d.id) === Number(grant.deptId));
          const workdayList = grant.breakdown.allDaysFormatted
            .filter(d => d.isWorkday)
            .map(d => d.fullName)
            .join(' - ');

          return (
            <div key={grant.id} className="bg-white dark:bg-jewel-bg border border-gray-200 dark:border-jewel-border rounded-xl overflow-hidden shadow-sm hover:border-blue-300 dark:hover:border-jewel-teal transition-all group">
              <div className="p-5">
                <div className="flex flex-wrap justify-between items-start gap-4">
                  <div className="flex-1 min-w-[300px]">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="relative">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-black text-sm shadow-inner group-hover:scale-110 transition-transform" 
                          style={{ backgroundColor: dept?.color || '#3B82F6' }}>
                          {(grant.user?.name || '??').split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="absolute -bottom-1 -right-1 bg-white dark:bg-jewel-surface p-1 rounded-lg shadow-sm border border-gray-100 dark:border-jewel-border">
                          <Shield className="h-3 w-3 text-purple-600 dark:text-jewel-ruby-tint" />
                        </div>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 dark:text-jewel-text flex items-center gap-2">
                          {grant.user?.name}
                          {grant.exceededLimit && <AlertCircle className="h-4 w-4 text-orange-500 dark:text-orange-400" />}
                        </h4>
                        <div className="flex items-center space-x-2">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-gray-100 dark:bg-jewel-surface text-gray-600 dark:text-jewel-text uppercase tracking-tighter">
                            {dept?.name || 'Χωρίς Τμήμα'}
                          </span>
                          <span className="text-gray-300 dark:text-jewel-text-muted">•</span>
                          <span className="text-[10px] font-bold text-gray-400 dark:text-jewel-text-muted uppercase tracking-widest flex items-center gap-1">
                            <Clock className="h-3 w-3" /> {grant.grantedDate.toLocaleDateString('el-GR')}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 dark:bg-jewel-bg/50 rounded-xl p-4 border border-gray-100 dark:border-jewel-border group-hover:bg-blue-50/30 dark:group-hover:bg-blue-900/20 transition-colors">
                        <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200/50 dark:border-jewel-border">
                          <div className="flex items-center space-x-2 text-[10px] font-bold text-gray-400 dark:text-jewel-text-muted uppercase">
                            <Calendar className="h-3 w-3" />
                            <span>Περίοδος</span>
                          </div>
                          <span className="text-[10px] font-black text-gray-500 dark:text-jewel-text-muted bg-gray-100 dark:bg-jewel-surface px-2 py-0.5 rounded uppercase">
                            {grant.calendarDays} ΗΜΕΡΟΛ. ({(grant.calendarDays - grant.workdays)} OFF)
                          </span>
                        </div>
                        
                        <div className="space-y-3">
                          <p className="text-sm font-bold text-gray-800 dark:text-jewel-text">
                            {grant.startDate.toLocaleDateString('el-GR', { weekday: 'long', day: 'numeric', month: 'long' })}
                            <span className="mx-2 text-gray-400 dark:text-jewel-text-muted font-normal">→</span>
                            {grant.endDate.toLocaleDateString('el-GR', { weekday: 'long', day: 'numeric', month: 'long' })}
                          </p>
                          
                          <div className="flex flex-wrap gap-1 mb-2">
                            {grant.breakdown.allDaysFormatted.map((day, i) => (
                              <div key={i} className="flex flex-col items-center">
                                <span className={`text-[9px] font-bold uppercase mb-1 ${day.isWorkday ? 'text-gray-400 dark:text-jewel-text-muted' : 'text-red-400 dark:text-red-500'}`}>
                                  {day.name}
                                </span>
                                <div className={`w-1.5 h-1.5 rounded-full ${day.isWorkday ? 'bg-blue-500' : 'bg-red-500'}`} />
                              </div>
                            ))}
                          </div>

                          <div className="bg-white/50 dark:bg-jewel-bg/50 p-2 rounded-lg border border-blue-50 dark:border-blue-900/50">
                            <p className="text-[9px] font-black text-blue-400 dark:text-jewel-teal-tint uppercase mb-1">Εργάσιμες Ημέρες:</p>
                            <p className="text-[10px] font-bold text-blue-700 dark:text-jewel-teal-tint leading-tight italic uppercase">
                              {workdayList}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col justify-center space-y-3">
                        <div className="bg-purple-50 dark:bg-jewel-ruby/20 rounded-xl p-4 border border-purple-100 dark:border-jewel-ruby">
                          <p className="text-[10px] font-bold text-purple-400 dark:text-jewel-ruby-tint uppercase mb-1">Αιτιολογία Χορήγησης</p>
                          <p className="text-sm text-purple-900 dark:text-purple-200 font-medium italic">"{grant.reason || 'Δεν ορίστηκε αιτιολογία'}"</p>
                        </div>
                        
                        {grant.grantedBy && (
                          <div className="flex items-center space-x-2 px-1">
                            <div className="w-5 h-5 rounded bg-blue-100 dark:bg-jewel-teal/30 flex items-center justify-center">
                              <User className="h-3 w-3 text-blue-600 dark:text-jewel-teal-tint" />
                            </div>
                            <span className="text-[10px] font-bold text-gray-500 dark:text-jewel-text-muted uppercase">
                              ΧΟΡΗΓΗΘΗΚΕ ΑΠΟ: <span className="text-gray-800 dark:text-jewel-text ml-1">{grant.grantedBy.name}</span>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end justify-between self-stretch min-w-[120px]">
                    <div className="text-right bg-white dark:bg-jewel-bg p-3 rounded-2xl border-2 border-purple-50 dark:border-purple-900/50 shadow-sm w-full">
                      <p className="text-[10px] font-black text-purple-300 dark:text-purple-500 uppercase leading-none mb-1 tracking-tighter">Εργάσιμες</p>
                      <div className="flex items-baseline justify-end space-x-1">
                        <span className="text-4xl font-black text-purple-600 dark:text-jewel-ruby-tint leading-none">{grant.daysGranted}</span>
                        <span className="text-xs font-bold text-purple-400 dark:text-purple-500">ημ.</span>
                      </div>
                    </div>

                    <button 
                      onClick={() => handlePrint(grant, dept)}
                      className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 dark:bg-jewel-surface hover:bg-gray-100 dark:hover:bg-jewel-surface text-gray-600 dark:text-jewel-text text-[10px] font-bold uppercase tracking-wider rounded-xl transition-colors border border-gray-200 dark:border-jewel-border"
                    >
                      <Download className="h-3 w-3" /> Εξαγωγή PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {visibleCount < filtered.length && (
        <div className="mt-8 flex justify-center">
          
          <button
            onClick={() => setVisibleCount(prev => prev + ITEMS_PER_PAGE)}
            className="px-6 py-3 bg-white dark:bg-jewel-bg border border-gray-200 dark:border-jewel-border hover:border-blue-300 dark:hover:border-jewel-teal text-blue-600 dark:text-jewel-teal-tint text-xs font-bold uppercase tracking-wider rounded-xl shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 flex items-center gap-2"
          >
            <History className="h-4 w-4" />
            Φόρτωση Επόμενων 25  / <span className={getStatusStyles()}>{remaining} Ακόμα</span>
          </button>
        </div>
      )}
    </div>
  );
};

const RequestsHistoryTab = ({ vacationRequests, userDatabase, departments, holidays }) => {
  const [selectedDepartments, setSelectedDepartments] = useState(departments.map(d => d.id));
  const [startDateFilter, setStartDateFilter] = useState(null);
  const [endDateFilter, setEndDateFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination State
  const ITEMS_PER_PAGE = 10;
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  // Reset pagination when filters change
  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
  }, [selectedDepartments, searchTerm, statusFilter, startDateFilter, endDateFilter]);

  const allResolved = useMemo(() => {
    if (!vacationRequests) return [];
    return vacationRequests
      .filter(req => {
        const s = req.status;
        const isGranted = req.manager_granted || req.managerGranted;
        if (isGranted) return false;
        if ((req.leave_type || req.leaveType) === 'unpaid') return false;
        return s === 'approved' || s === 'rejected' || s === 'manager1_rejected';
      })
      .map(req => {
        const userId = req.user_id || req.userId;
        const user = userDatabase.find(u => Number(u.id) === Number(userId));
        const start = new Date(req.start_date || req.startDate);
        const end = new Date(req.end_date || req.endDate);
        const bd = getDayBreakdown(start, end, holidays);
        const reviewer = userDatabase.find(u => Number(u.id) === Number(req.reviewed_by || req.reviewedBy));

        return {
          id: req.id,
          user,
          reviewer,
          deptId: user?.department_id || user?.departmentId,
          status: req.status,
          workdays: bd.workdays,
          daysGranted: bd.workdays,
          calendarDays: bd.calendarDays,
          breakdown: bd,
          reason: req.reason,
          startDate: start,
          endDate: end,
          reviewedDate: (req.reviewed_date || req.reviewedDate) ? new Date(req.reviewed_date || req.reviewedDate) : null,
          createdDate: new Date(req.created_at || req.createdAt)
        };
      })
      .sort((a, b) => b.createdDate - a.createdDate);
  }, [vacationRequests, userDatabase, holidays]);

  const filtered = useMemo(() => allResolved.filter(r => {
    const matchesDept = selectedDepartments.includes(Number(r.deptId));
    const matchesSearch = !searchTerm || (r.user?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
    const matchesStart = !startDateFilter || r.startDate >= startDateFilter;
    const matchesEnd = !endDateFilter || r.endDate <= endDateFilter;
    return matchesDept && matchesSearch && matchesStatus && matchesStart && matchesEnd;
  }), [allResolved, selectedDepartments, searchTerm, statusFilter, startDateFilter, endDateFilter]);

  const visibleRequests = filtered.slice(0, visibleCount);
  const remaining = filtered.length - visibleCount;

  const getStatusStyles = () => {
    if (remaining >= 300) return "text-red-500 font-medium";
    if (remaining >= 200 && remaining < 300) return "text-orange-500 font-bold";
    if (remaining >= 100 && remaining < 200)   return "text-yellow-500 font-bold";
    if (remaining >= 50 && remaining < 100) return "text-teal-500 font-bold";
    if (remaining >= 10 && remaining < 50)   return "text-green-500 font-bold";
    return "text-black font-medium animate-bounce";
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-wrap gap-2 mb-6 p-1 bg-gray-100 dark:bg-jewel-bg rounded-xl w-fit">
        {[
          { id: 'all', label: 'Όλα', icon: History },
          { id: 'approved', label: 'Εγκεκριμένα', icon: CheckCircle },
          { id: 'rejected', label: 'Απορριφθέντα', icon: X }
        ].map(btn => (
          <button
            key={btn.id}
            onClick={() => setStatusFilter(btn.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
              statusFilter === btn.id ? 'bg-white dark:bg-jewel-surface text-blue-600 dark:text-jewel-teal-tint shadow-sm scale-105' : 'text-gray-500 dark:text-jewel-text-muted hover:text-gray-700 dark:hover:text-jewel-text'
            }`}
          >
            <btn.icon className="h-3 w-3" />
            {btn.label}
          </button>
        ))}
      </div>

      <FilterBar searchTerm={searchTerm} onSearch={setSearchTerm} departments={departments} selectedDepartments={selectedDepartments} onDeptChange={setSelectedDepartments} startDate={startDateFilter} endDate={endDateFilter} onStartDate={setStartDateFilter} onEndDate={setEndDateFilter} />

      <div className="grid grid-cols-1 gap-4">
        {visibleRequests.map(req => {
          const dept = departments.find(d => Number(d.id) === Number(req.deptId));
          const workdayList = req.breakdown.allDaysFormatted
            .filter(d => d.isWorkday)
            .map(d => d.fullName)
            .join(' - ');

          return (
            <div key={req.id} className="bg-white dark:bg-jewel-bg border border-gray-200 dark:border-jewel-border rounded-2xl overflow-hidden shadow-sm hover:border-blue-200 dark:hover:border-jewel-teal transition-all">
              <div className="p-6">
                <div className="flex flex-col sm:flex-row justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: dept?.color || '#3B82F6' }}>
                          {(req.user?.name || '??').split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 dark:text-jewel-text leading-none mb-1">{req.user?.name}</h4>
                          <span className="text-[10px] font-bold text-gray-400 dark:text-jewel-text-muted uppercase tracking-widest">{dept?.name}</span>
                        </div>
                      </div>
                      <div className={`px-3 py-1 rounded-full border text-[10px] font-black uppercase ${req.status === 'approved' ? 'bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800'}`}>
                        {req.status === 'approved' ? 'ΕΓΚΡΙΘΗΚΕ' : 'ΑΠΟΡΡΙΦΘΗΚΕ'}
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-jewel-bg/50 rounded-2xl p-4 border border-gray-100 dark:border-jewel-border">
                      <div className="flex items-center gap-4 py-2 border-b border-gray-200/50 dark:border-jewel-border mb-4">
                        <div className="flex-1">
                          <p className="text-[9px] font-black text-gray-400 dark:text-jewel-text-muted uppercase mb-1">Περίοδος</p>
                          <p className="text-xs font-bold text-gray-700 dark:text-jewel-text">
                            {req.startDate.toLocaleDateString('el-GR')} έως {req.endDate.toLocaleDateString('el-GR')}
                          </p>
                          <p className="text-[10px] font-bold text-blue-600 dark:text-jewel-teal-tint mt-1 uppercase italic">{workdayList}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[9px] font-black text-blue-400 dark:text-jewel-teal-tint uppercase mb-1">Εργάσιμες / Ημερολ.</p>
                          <p className="text-xl font-black text-blue-600 dark:text-jewel-teal-tint">{req.workdays} / {req.calendarDays}</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-jewel-text italic">"{req.reason || 'Χωρίς σχόλιο'}"</p>
                    </div>
                  </div>

                  <div className="sm:w-48 flex flex-col justify-end">
                    <button onClick={() => handlePrint(req, dept)} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 dark:bg-jewel-surface hover:bg-gray-100 dark:hover:bg-jewel-surface text-gray-600 dark:text-jewel-text text-[10px] font-bold uppercase tracking-wider rounded-xl transition-colors border border-gray-200 dark:border-jewel-border">
                      <Download className="h-3 w-3" /> Εξαγωγή PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {visibleCount < filtered.length && (
        <div className="mt-8 flex justify-center">
          <button
            onClick={() => setVisibleCount(prev => prev + ITEMS_PER_PAGE)}
            className="px-6 py-3 bg-white dark:bg-jewel-bg border border-gray-200 dark:border-jewel-border hover:border-blue-300 dark:hover:border-jewel-teal text-blue-600 dark:text-jewel-teal-tint text-xs font-bold uppercase tracking-wider rounded-xl shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 flex items-center gap-2"
          >
            <History className="h-4 w-4" />
            Φόρτωση Επόμενων 25  / <span className={getStatusStyles()}>{remaining} Ακόμα</span>
          </button>
          
        </div>
      )}
    </div>
  );
};

const UnpaidTab = ({ vacationRequests, userDatabase, departments, holidays }) => {
  const [selectedDepartments, setSelectedDepartments] = useState(departments.map(d => d.id));
  const [startDateFilter, setStartDateFilter] = useState(null);
  const [endDateFilter, setEndDateFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const ITEMS_PER_PAGE = 25;
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
  }, [selectedDepartments, searchTerm, statusFilter, startDateFilter, endDateFilter]);

  const allUnpaid = useMemo(() => {
    if (!vacationRequests) return [];
    return vacationRequests
      .filter(req => (req.leave_type || req.leaveType) === 'unpaid')
      .map(req => {
        const userId = req.user_id || req.userId;
        const user = userDatabase.find(u => Number(u.id) === Number(userId));
        const start = new Date(req.start_date || req.startDate);
        const end = new Date(req.end_date || req.endDate);
        const bd = getDayBreakdown(start, end, holidays);
        const reviewer = userDatabase.find(u => Number(u.id) === Number(req.reviewed_by || req.reviewedBy));
        const isGranted = req.manager_granted || req.managerGranted;

        return {
          id: req.id,
          user,
          reviewer,
          deptId: user?.department_id || user?.departmentId,
          status: req.status,
          isGranted,
          workdays: bd.workdays,
          daysGranted: bd.workdays,
          calendarDays: bd.calendarDays,
          breakdown: bd,
          reason: req.reason,
          startDate: start,
          endDate: end,
          reviewedDate: (req.reviewed_date || req.reviewedDate) ? new Date(req.reviewed_date || req.reviewedDate) : null,
          createdDate: new Date(req.created_at || req.createdAt)
        };
      })
      .sort((a, b) => b.createdDate - a.createdDate);
  }, [vacationRequests, userDatabase, holidays]);

  const filtered = useMemo(() => allUnpaid.filter(r => {
    const matchesDept = selectedDepartments.includes(Number(r.deptId));
    const matchesSearch = !searchTerm || (r.user?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
    const matchesStart = !startDateFilter || r.startDate >= startDateFilter;
    const matchesEnd = !endDateFilter || r.endDate <= endDateFilter;
    return matchesDept && matchesSearch && matchesStatus && matchesStart && matchesEnd;
  }), [allUnpaid, selectedDepartments, searchTerm, statusFilter, startDateFilter, endDateFilter]);

  const stats = useMemo(() => ({
    total: filtered.length,
    totalDays: filtered.reduce((acc, curr) => acc + curr.daysGranted, 0),
    fromManager: filtered.filter(r => r.isGranted).length,
    fromRequest: filtered.filter(r => !r.isGranted).length
  }), [filtered]);

  const visibleRequests = filtered.slice(0, visibleCount);
  const remaining = filtered.length - visibleCount;

  const getStatusStyles = () => {
    if (remaining >= 300) return "text-red-500 font-medium";
    if (remaining >= 200 && remaining < 300) return "text-orange-500 font-bold";
    if (remaining >= 100 && remaining < 200) return "text-yellow-500 font-bold";
    if (remaining >= 50 && remaining < 100) return "text-teal-500 font-bold";
    if (remaining >= 10 && remaining < 50) return "text-green-500 font-bold";
    return "text-black font-medium animate-bounce";
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Σύνολο Άνευ Αποδοχών', value: stats.total, icon: FileText, color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/40' },
          { label: 'Ημέρες Άνευ Αποδοχών', value: stats.totalDays, icon: Calendar, color: 'text-purple-600 dark:text-jewel-ruby-tint', bg: 'bg-purple-50 dark:bg-jewel-ruby/25' },
          { label: 'Από Διαχειριστή', value: stats.fromManager, icon: Shield, color: 'text-blue-600 dark:text-jewel-teal-tint', bg: 'bg-blue-50 dark:bg-jewel-teal/25' },
          { label: 'Από Αίτημα', value: stats.fromRequest, icon: User, color: 'text-gray-600 dark:text-jewel-text', bg: 'bg-gray-50 dark:bg-jewel-bg' }
        ].map((stat, idx) => (
          <div key={idx} className="bg-white dark:bg-jewel-bg p-4 rounded-xl border border-gray-200 dark:border-jewel-border shadow-sm transition-all hover:shadow-md">
            <div className="flex items-center justify-between mb-2">
              <stat.icon className={`h-4 w-4 ${stat.color}`} />
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${stat.bg} ${stat.color}`}>ΣΤΑΤΙΣΤΙΚΑ</span>
            </div>
            <p className="text-[10px] font-bold text-gray-400 dark:text-jewel-text-muted uppercase tracking-wider">{stat.label}</p>
            <p className={`text-2xl font-black ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-6 p-1 bg-gray-100 dark:bg-jewel-bg rounded-xl w-fit">
        {[
          { id: 'all', label: 'Όλα', icon: History },
          { id: 'approved', label: 'Εγκεκριμένα', icon: CheckCircle },
          { id: 'rejected', label: 'Απορριφθέντα', icon: X }
        ].map(btn => (
          <button
            key={btn.id}
            onClick={() => setStatusFilter(btn.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
              statusFilter === btn.id ? 'bg-white dark:bg-jewel-surface text-orange-600 dark:text-orange-400 shadow-sm scale-105' : 'text-gray-500 dark:text-jewel-text-muted hover:text-gray-700 dark:hover:text-jewel-text'
            }`}
          >
            <btn.icon className="h-3 w-3" />
            {btn.label}
          </button>
        ))}
      </div>

      <FilterBar searchTerm={searchTerm} onSearch={setSearchTerm} departments={departments} selectedDepartments={selectedDepartments} onDeptChange={setSelectedDepartments} startDate={startDateFilter} endDate={endDateFilter} onStartDate={setStartDateFilter} onEndDate={setEndDateFilter} />

      <div className="grid grid-cols-1 gap-4">
        {visibleRequests.map(req => {
          const dept = departments.find(d => Number(d.id) === Number(req.deptId));
          const workdayList = req.breakdown.allDaysFormatted
            .filter(d => d.isWorkday)
            .map(d => d.fullName)
            .join(' - ');

          return (
            <div key={req.id} className="bg-white dark:bg-jewel-bg border border-gray-200 dark:border-jewel-border rounded-2xl overflow-hidden shadow-sm hover:border-orange-200 dark:hover:border-orange-700 transition-all">
              <div className="p-6">
                <div className="flex flex-col sm:flex-row justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: dept?.color || '#3B82F6' }}>
                          {(req.user?.name || '??').split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 dark:text-jewel-text leading-none mb-1">{req.user?.name}</h4>
                          <span className="text-[10px] font-bold text-gray-400 dark:text-jewel-text-muted uppercase tracking-widest">{dept?.name}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="px-3 py-1 rounded-full border text-[10px] font-black uppercase bg-orange-50 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800">
                          ΑΝΕΥ ΑΠΟΔΟΧΩΝ
                        </div>
                        <div className={`px-3 py-1 rounded-full border text-[10px] font-black uppercase ${req.status === 'approved' ? 'bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800'}`}>
                          {req.status === 'approved' ? 'ΕΓΚΡΙΘΗΚΕ' : 'ΑΠΟΡΡΙΦΘΗΚΕ'}
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-jewel-bg/50 rounded-2xl p-4 border border-gray-100 dark:border-jewel-border">
                      <div className="flex items-center gap-4 py-2 border-b border-gray-200/50 dark:border-jewel-border mb-4">
                        <div className="flex-1">
                          <p className="text-[9px] font-black text-gray-400 dark:text-jewel-text-muted uppercase mb-1">Περίοδος</p>
                          <p className="text-xs font-bold text-gray-700 dark:text-jewel-text">
                            {req.startDate.toLocaleDateString('el-GR')} έως {req.endDate.toLocaleDateString('el-GR')}
                          </p>
                          <p className="text-[10px] font-bold text-orange-600 dark:text-orange-400 mt-1 uppercase italic">{workdayList}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[9px] font-black text-orange-400 dark:text-orange-300 uppercase mb-1">Εργάσιμες / Ημερολ.</p>
                          <p className="text-xl font-black text-orange-600 dark:text-orange-400">{req.workdays} / {req.calendarDays}</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-jewel-text italic">"{req.reason || 'Χωρίς σχόλιο'}"</p>
                      {req.isGranted && req.reviewer && (
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200/50 dark:border-jewel-border">
                          <Shield className="h-3 w-3 text-blue-500 dark:text-jewel-teal-tint" />
                          <span className="text-[10px] font-bold text-gray-500 dark:text-jewel-text-muted uppercase">
                            ΧΟΡΗΓΗΘΗΚΕ ΑΠΟ: <span className="text-gray-800 dark:text-jewel-text">{req.reviewer.name}</span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="sm:w-48 flex flex-col justify-end">
                    <button onClick={() => handlePrint(req, dept)} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 dark:bg-jewel-surface hover:bg-gray-100 dark:hover:bg-jewel-surface text-gray-600 dark:text-jewel-text text-[10px] font-bold uppercase tracking-wider rounded-xl transition-colors border border-gray-200 dark:border-jewel-border">
                      <Download className="h-3 w-3" /> Εξαγωγή PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {visibleCount < filtered.length && (
        <div className="mt-8 flex justify-center">
          <button
            onClick={() => setVisibleCount(prev => prev + ITEMS_PER_PAGE)}
            className="px-6 py-3 bg-white dark:bg-jewel-bg border border-gray-200 dark:border-jewel-border hover:border-orange-300 dark:hover:border-orange-600 text-orange-600 dark:text-orange-400 text-xs font-bold uppercase tracking-wider rounded-xl shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 flex items-center gap-2"
          >
            <History className="h-4 w-4" />
            Φόρτωση Επόμενων 25 / <span className={getStatusStyles()}>{remaining} Ακόμα</span>
          </button>
        </div>
      )}
    </div>
  );
};

const HistoryView = ({ currentUser, vacationRequests, userDatabase, departments, holidays }) => {
  const [subTab, setSubTab] = useState('granted');
  const [showMassExport, setShowMassExport] = useState(false);

  return (
    <div className="max-w-6xl mx-auto space-y-6 min-h-[80vh]">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-blue-600 p-2 rounded-xl shadow-lg shadow-blue-200 dark:shadow-jewel-teal/30">
            <History className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-gray-900 dark:text-jewel-text leading-tight">
              Ιστορικό Αδειών
            </h2>
            <p className="text-sm text-gray-500 dark:text-jewel-text-muted font-medium">Διαχείριση και επισκόπηση όλων των αποφάσεων</p>
          </div>
          <button
            onClick={() => setShowMassExport(true)}
            className="ml-4 flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-jewel-teal/25 text-blue-700 dark:text-jewel-teal-tint text-xs font-black uppercase tracking-widest rounded-xl border border-blue-100 dark:border-jewel-teal hover:bg-blue-100 dark:hover:bg-jewel-teal/35 transition-all shadow-sm active:scale-95"
          >
            <FileText className="h-4 w-4" />
            Μαζική Εξαγωγή
          </button>
        </div>
        
        <div className="flex items-center gap-2 bg-white dark:bg-jewel-bg p-1.5 rounded-2xl shadow-sm border border-gray-200 dark:border-jewel-border">
          <button
            onClick={() => setSubTab('granted')}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all duration-300 ${
              subTab === 'granted'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-100 dark:shadow-jewel-teal/30 scale-105'
                : 'text-gray-400 dark:text-jewel-text-muted hover:text-gray-600 dark:hover:text-jewel-text hover:bg-gray-50 dark:hover:bg-jewel-surface'
            }`}
          >
            🏢 Χορηγήσεις
          </button>
          <button
            onClick={() => setSubTab('requests')}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all duration-300 ${
              subTab === 'requests'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-100 dark:shadow-jewel-teal/30 scale-105'
                : 'text-gray-400 dark:text-jewel-text-muted hover:text-gray-600 dark:hover:text-jewel-text hover:bg-gray-50 dark:hover:bg-jewel-surface'
            }`}
          >
            📋 Αιτήματα
          </button>
          <button
            onClick={() => setSubTab('unpaid')}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all duration-300 ${
              subTab === 'unpaid'
                ? 'bg-orange-600 text-white shadow-md shadow-orange-100 dark:shadow-orange-900/40 scale-105'
                : 'text-gray-400 dark:text-jewel-text-muted hover:text-gray-600 dark:hover:text-jewel-text hover:bg-gray-50 dark:hover:bg-jewel-surface'
            }`}
          >
            🚫 Άνευ Αποδοχών
          </button>
        </div>
      </div>

      <div className="relative">
        {subTab === 'granted' ? (
          <GrantedTab vacationRequests={vacationRequests} userDatabase={userDatabase} departments={departments} holidays={holidays} />
        ) : subTab === 'requests' ? (
          <RequestsHistoryTab vacationRequests={vacationRequests} userDatabase={userDatabase} departments={departments} holidays={holidays} />
        ) : (
          <UnpaidTab vacationRequests={vacationRequests} userDatabase={userDatabase} departments={departments} holidays={holidays} />
        )}
      </div>

      {showMassExport && (
        <MassExportModal
          isOpen={showMassExport}
          onClose={() => setShowMassExport(false)}
          vacationRequests={vacationRequests}
          userDatabase={userDatabase}
          departments={departments}
          holidays={holidays}
        />
      )}
    </div>
  );
};

export default HistoryView;